import { Avatar, ModalV2, Text } from 'components';
import { getModerationContent, theme } from 'lib';
import { useEffect, useState } from 'react';
import { fmtDate, s3Url } from '../constants';
import { LoadingBlock } from './ui';

const KIND_LABELS = {
  Post: 'Post',
  Comment: 'Comment',
  Reply: 'Reply',
  NewsView: 'News view',
  SportChat: 'Fan chat',
  Fancam: 'Fancam',
  PredictionItem: 'Prediction',
  User: 'User'
};

/** Best-effort text of an arbitrary content doc (mirrors resolveContentPreview). */
const docText = (doc = {}) =>
  doc.body ||
  doc.text ||
  doc.message ||
  doc.caption ||
  doc.description ||
  doc.comment ||
  doc.content ||
  doc.title ||
  doc.username ||
  null;

const docImage = (doc = {}) =>
  s3Url(doc.s3Key || doc.image || doc.media?.[0]?.uri || doc.photo || doc.avatar || null);

const isPopulated = (v) => v && typeof v === 'object' && (v.name || v.username || v.email);

/**
 * Labeled people involved in the content: poster, shoutout target, athlete,
 * team — whichever refs the document carries.
 */
const peopleOf = (doc = {}) => {
  const people = [];
  const poster = isPopulated(doc.from) ? doc.from : isPopulated(doc.owner) ? doc.owner : null;
  if (poster) people.push({ label: 'Posted by', person: poster });

  if (isPopulated(doc.user)) {
    // `user` is the shoutout target when a separate poster exists, else the author.
    people.push({ label: poster ? 'To' : 'Posted by', person: doc.user });
  }
  if (isPopulated(doc.athlete)) people.push({ label: 'Athlete', person: doc.athlete });
  if (isPopulated(doc.team)) people.push({ label: 'Team', person: doc.team });
  // Guard-time targets, carried by the log when the content was blocked pre-save.
  (doc.contextUsers || []).filter(isPopulated).forEach((u) => people.push({ label: 'To', person: u }));
  return people;
};

const PersonChip = ({ label, person }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide">
      {label}
    </span>
    <Avatar user={person} avatar={person.avatar} tiny />
    <span className="text-[12px] text-[#353E4A]">
      {person.name || person.username || person.email || '—'}
      {person.username && <span className="text-[#97A3B5]"> @{person.username}</span>}
      {person.type === 'team' && <span className="text-[#97A3B5]"> · team</span>}
    </span>
  </div>
);

const PeopleRow = ({ doc }) => {
  const people = peopleOf(doc);
  if (!people.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-2">
      {people.map(({ label, person }) => (
        <PersonChip key={`${label}-${person._id || person.username}`} label={label} person={person} />
      ))}
    </div>
  );
};

const ChainCard = ({ kind, doc, highlight }) => {
  const text = docText(doc);
  const image = docImage(doc);
  return (
    <div
      className={`rounded-xl border p-3.5 ${
        highlight ? 'border-[#FA3F58]/40 bg-[#FFF7F8]' : 'border-[#D2D8DF] bg-[#FAFBFC]'
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span
          className={`text-[10px] font-semibold uppercase tracking-wide ${
            highlight ? 'text-[#FA3F58]' : 'text-[#97A3B5]'
          }`}
        >
          {KIND_LABELS[kind] || kind}
          {highlight && ' · moderated'}
        </span>
        {doc?.createdAt && (
          <span className="text-[10.5px] text-[#97A3B5]">{fmtDate(doc.createdAt)}</span>
        )}
      </div>
      <PeopleRow doc={doc} />
      {text && (
        <p className="text-[13px] leading-relaxed text-[#353E4A] whitespace-pre-wrap break-words max-h-[180px] overflow-auto m-0">
          {text}
        </p>
      )}
      {image && (
        <a href={image} target="_blank" rel="noreferrer">
          <img
            src={image}
            alt="content"
            className="mt-2 w-20 h-20 rounded-lg object-cover border border-[#D2D8DF]"
          />
        </a>
      )}
      {!text && !image && (
        <span className="text-[12px] italic text-[#97A3B5]">No preview available.</span>
      )}
    </div>
  );
};

/** Full content context of a log row: root entity -> parent comment -> moderated doc. */
const ContentModal = ({ logId, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!logId) return;
    setData(null);
    setError(null);
    (async () => {
      const res = await getModerationContent(logId);
      if (res?.err || !res?.data) setError(res?.err || 'Could not load the content.');
      else setData(res.data);
    })();
  }, [logId]);

  const log = data?.log;
  const chain = data?.chain || [];

  return (
    <ModalV2
      visible={!!logId}
      closeModal={onClose}
      width="560px"
      overflowY
      header={
        <Text h4 bold marg="0">
          Moderated content
        </Text>
      }
    >
      <div className="flex flex-col gap-3 p-6">
        {error ? (
          <Text c1 col={theme.SECONDARY} marg="0" style={{ fontSize: '13px' }}>
            {error}
          </Text>
        ) : !data ? (
          <LoadingBlock height="140px" />
        ) : (
          <>
            {chain.map(({ kind, doc }, i) => (
              <ChainCard
                key={doc?._id || i}
                kind={kind}
                doc={doc}
                highlight={i === chain.length - 1}
              />
            ))}

            {!chain.length && (
              <>
                {log?.textSnippet && (
                  <ChainCard
                    kind={log.contentType}
                    doc={{
                      body: log.textSnippet,
                      createdAt: log.createdAt,
                      owner: log.user,
                      contextUsers: log.contextUsers
                    }}
                    highlight
                  />
                )}
                <span className="text-[12px] italic text-[#97A3B5]">
                  {log?.textSnippet
                    ? 'The content was blocked before saving — showing the analyzed text.'
                    : 'The underlying content is unavailable (deleted or never saved).'}
                </span>
              </>
            )}

            {log?.matchedTerms?.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-semibold text-[#97A3B5] uppercase tracking-wide">
                  Matched terms
                </span>
                {log.matchedTerms.map((t) => (
                  <span
                    key={t}
                    className="text-[11px] font-mono text-[#E11D48] bg-[#FFF1F2] border border-[#FECDD3] rounded-full px-2 py-0.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </ModalV2>
  );
};

export default ContentModal;
